<template>
  <div class="ProfileCard pt-4">
    <p class="d-flex align-items-center justify-content-between px-4">
      <strong>
        {{ $t(`${$options.name}.title`) }}
      </strong>
      <span @click="logout">
        <span>{{ $t(`${$options.name}.logout`) }}</span>
        <i class="icon fa-fw fal fa-arrow-right-from-bracket ml-2" />
      </span>
    </p>
    <!-- TEMP. disable until all translations are done -->
    <div id="locales">
      <div class="d-flex align-items-center">
        <strong class="mb-1">{{ $t('language') }}</strong>
      </div>
      <div class="radio-section">
        <div
          v-for="locale in locales"
          :key="locale"
        >
          <input
            :id="`language-${locale}-radio`"
            v-model="selectedLanguage"
            type="radio"
            :value="locale"
            @click="selectLanguage(locale)"
          >
          <label
            :for="`language-${locale}-radio`"
            class="ml-2 mb-0"
          >
            {{ $t(`languages.${locale}`) }}
          </label>
        </div>
      </div>
    </div>
    <p class="d-flex align-items-start px-4">
      <i class="icon fa-fw fal fa-user mr-2" />

      <span class="d-flex flex-wrap flex-shrink-1 flex-column">
        {{ username }}
        <small v-if="organization">{{ $t(`${$options.name}.organization`) }}: {{ organization }}</small>
      </span>
    </p>
    <p
      v-if="email"
      class="d-flex align-items-center px-4"
    >
      <i class="icon fa-fw fal fa-envelope mr-2" />
      <span class="d-flex flex-wrap flex-shrink-1">
        {{ email }}
      </span>
    </p>
    <div class="mx-4 mb-3">
      <div class="d-flex border-bottom mb-2">
        <strong class="flex-25">{{ $t(`${$options.name}.role`) }}</strong>
        <strong class="flex-35">
          {{ $t(`${$options.name}.${isCaseViewer ? 'customers' : 'localAuthorities'}`) }}
        </strong>
        <strong class="flex-35">{{ $t(`${$options.name}.accessTo`) }}</strong>
      </div>
      <div
        v-if="activeRole"
        class="d-flex mb-2"
      >
        <ul class="pl-3 flex-25">
          <li>
            {{ $t(`${$options.name}.userRoles.${activeRole}`) }}
          </li>
        </ul>
        <ul class="pl-3 flex-35">
          <li
            v-for="role in roles[activeRole]"
            :key="role.value || role"
          >
            <div>
              <span v-if="role === '*'">{{ $t('ProfileCard.allLocalAuthorities') }}</span>
              <span v-else-if="isCaseViewer">{{ role.label[$i18n.locale] || role.label }}</span>
              <span v-else>{{ role.text }} ({{ role.value }})</span>
            </div>
          </li>
        </ul>

        <ul class="pl-3 flex-35">
          <li
            v-for="feature in getRoleFeatures({ role: activeRole })"
            :key="feature"
          >
            {{ feature }}
          </li>
        </ul>
      </div>
    </div>
    <PasswordBox @processing="handleProcessing" />
  </div>
</template>

<script>
import PasswordBox from '@/components/profile/PasswordBox.vue';
import { mapGetters } from 'vuex';
import recordList from '@/helpers/recordList';

export default {
  name: 'ProfileCard',
  components: { PasswordBox },
  data() {
    return {
      roles: {
        admin: [],
        cpo: [],
        municipality: [],
        contractor: [],
        gridoperator: [],
        region: [],
        // RVB //
        developer: [],
        caseworker: [],
        casevieuwer: [],
        customer: []
      },
      locales: ['en', 'nl', 'de', 'fr'],
      selectedLanguage: localStorage.getItem('workflow-language') || 'en'
    };
  },
  computed: {
    ...mapGetters('tenant', ['getFeatureAccess', 'getMunicipalityOptions', 'getTenantStakeholders']),

    activeRole() {
      if (!this.tenantRoles) return;
      return Object.keys(this.tenantRoles).find(key => key !== 'tenant' && this.tenantRoles[key].length);
    },
    username() {
      return this.$auth.user.name;
    },
    identity() {
      return this.$auth.user['https://evtools.nl/identity'];
    },
    email() {
      return this.$auth.user.email || this.$auth.user['https://evtools.nl/email'];
    },
    organization() {
      return this.identity?.uuid && `${this.identity?.label} (${this.identity?.category})`;
    },
    isCaseViewer() {
      return this.tenantRoles?.caseviewer;
    },
    isCustomerIdentity() {
      return this.identity.category === 'customer';
    },
    caseViewerCustomers() {
      if (!this.tenantRoles?.caseviewer) return;

      const isCaseViwer = this.tenantRoles.caseviewer;
      const customers = recordList.setCustomerDropdown();
      return isCaseViwer ? customers.filter(customer => this.tenantRoles.caseviewer.includes(customer.value)) : null;
    },
    tenantRoles() {
      // TEMP: to fix showstopper TOOLS-7230 => role municipalityCodes => uuid's //
      const userRoles = this.$auth.user['https://evtools.nl/roles'].find(role => role.tenant === process.env.VUE_APP_TENANT)
      const tenantRoles = userRoles ? JSON.parse(JSON.stringify(userRoles)) : {}
      const role = this.identity?.category || Object.keys(tenantRoles)?.[0]

      if (!tenantRoles[role]?.includes('*')) {
        const roles = this.getTenantStakeholders({ category: 'municipality' }).filter(s => tenantRoles[role]?.includes(s.uuid))

        if (roles.length) {
          tenantRoles[role] = roles.map(r => r.code)
        }
      }

      return tenantRoles
    }
  },
  created() {
    if (!this.tenantRoles) return;

    Object.keys(this.roles).forEach(key => {
      this.roles[key] = this.tenantRoles[key]?.filter(this.filterRole)?.map(code => {
        return code === '*' ? '*' : this.getMunicipalityOptions.find(mun => mun.value === code);
      });

      if (!this.roles[key]?.length && !this.tenantRoles[key]) {
         delete this.roles[key];
      }
    });

    if (this.caseViewerCustomers) {
      this.roles.caseviewer = this.caseViewerCustomers;
    }

    this.$store.dispatch('user/setCurrentUser', { user: this.roles });
  },
  methods: {
    selectLanguage(language) {
      this.$i18n.locale = language;

      localStorage.setItem('workflow-language', language);
    },
    filterRole(code) {
      return code === '*' || this.getMunicipalityOptions.some(m => m.value === code);
    },
    handleProcessing({ busy }) {
      if (busy) {
        this.freezeOverlay();
      } else {
        this.unfreezeOverlay();
      }
    },
    logout() {
      this.$auth.logout({
        returnTo: window.location.origin
      });
    },
    getRoleFeatures({ role }) {
      const roleFeatures = Object.keys(this.getFeatureAccess).filter(key => this.getFeatureAccess[key].includes(role));
      const featureTranslations = `${this.$options.name}.featureAccess`;

      return roleFeatures.map(feature => this.$i18n.t(`${featureTranslations}.${feature}`));
    }
  }
};
</script>

<style scoped lang="scss">
.ProfileCard {
  position: relative;
  background: white;
  z-index: 999999;
  opacity: 0.999;
  min-width: 800px;
  max-width: 800px;
  box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;

  p {
    font-size: 1.15rem;
    cursor: auto;
  }
  &__logout,
  &__logout {
    cursor: pointer !important;

    &:hover {
      color: #b04300;
    }
  }
  .icon {
    font-size: 1.3rem;
    color: black;
  }

  #locales {
    position: absolute;
    left: 670px;
    top: 60px;
  }
}
</style>
